// Product rendering and handling

import { state } from '../state';
import type { Product } from '../types';
import { getTotalItemCount } from '../utils/calculations';
import { updateCartBadge } from '../ui/cart';
import { showViewCartPrompt } from '../ui/toast';
import { updateOrderSummary, updateCheckoutButton } from './order-summary';

export function renderSkeletonProducts(count: number = 4): string {
    return Array(count).fill(0).map(() => `
        <div class="skeleton-card">
            <div class="skeleton-image"></div>
            <div style="padding: var(--spacing-md);">
                <div class="skeleton-text long"></div>
                <div class="skeleton-text medium"></div>
                <div class="skeleton-text short"></div>
            </div>
        </div>
    `).join('');
}

export async function loadProducts() {
    try {
        const response = await fetch('/api/products');
        const data = await response.json();

        if (data.success && data.data) {
            state.products = data.data;
            renderProducts();
        } else {
            throw new Error('Failed to load products');
        }
    } catch (error) {
        console.error('Error loading products:', error);
        const grid = document.getElementById('products-grid');
        if (grid) {
            grid.innerHTML = `
                <div class="col-span-full">
                    <div class="hero bg-base-200 rounded-box py-16">
                        <div class="hero-content text-center">
                            <div class="max-w-md">
                                <div class="text-6xl mb-4 opacity-50">⚠️</div>
                                <h3 class="text-2xl font-bold mb-2">Unable to Load Products</h3>
                                <p class="mb-6 opacity-70">We're having trouble loading the products. Please check your connection and try again.</p>
                                <button class="btn btn-primary" onclick="location.reload()">
                                    Refresh Page
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }
    }
}

export function renderProducts() {
    const grid = document.getElementById('products-grid');
    if (!grid) return;

    grid.innerHTML = state.products.map((product, index) => `
        <div class="product-card scale-in" style="animation-delay: ${index * 0.1}s" role="article" aria-label="${product.name}">
            <figure class="relative overflow-hidden h-72 bg-base-200">
                <img
                    src="${product.image_url}"
                    alt="${product.name}"
                    class="product-image w-full h-full object-cover cursor-pointer transition-opacity hover:opacity-90"
                    loading="lazy"
                    data-product-id="${product.id}"
                />
                <div class="absolute top-4 right-4">
                    <div class="badge badge-primary shadow-lg">Limited</div>
                </div>
            </figure>
            <div class="p-6">
                <h3 class="text-xl font-bold mb-2">${product.name}</h3>
                <p class="text-sm opacity-70 mb-4 line-clamp-2">${product.description}</p>

                <div class="flex items-center justify-between mb-4">
                    <div>
                        <div class="text-xs opacity-60 mb-1">Price</div>
                        <div class="text-2xl font-bold text-primary" aria-label="Price: ${product.formatted_price}">
                            ${product.formatted_price}
                        </div>
                    </div>
                </div>

                <div class="flex gap-2" role="group" aria-label="Quantity selector for ${product.name}">
                    <button
                        type="button"
                        class="btn btn-circle btn-sm hover:btn-primary qty-btn"
                        data-product-id="${product.id}"
                        data-action="decrease"
                        aria-label="Decrease quantity"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                        </svg>
                    </button>
                    <input
                        type="number"
                        class="input input-bordered input-sm text-center font-bold flex-1 qty-input"
                        id="qty-${product.id}"
                        value="0"
                        min="0"
                        max="10"
                        readonly
                        aria-label="Quantity for ${product.name}"
                    />
                    <button
                        type="button"
                        class="btn btn-circle btn-sm btn-primary qty-btn"
                        data-product-id="${product.id}"
                        data-action="increase"
                        aria-label="Increase quantity"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    `).join('');

    // Attach quantity selector listeners
    document.querySelectorAll('.qty-btn').forEach(btn => {
        btn.addEventListener('click', handleQuantityChange);
    });

    // Attach image preview listeners
    document.querySelectorAll('.product-image').forEach(img => {
        img.addEventListener('click', handleImagePreview);
    });
}

function handleQuantityChange(e: Event) {
    // Get the button element, even if SVG/path was clicked
    let button = e.target as HTMLElement;
    if (!button.classList.contains('qty-btn')) {
        button = button.closest('.qty-btn') as HTMLElement;
    }

    if (!button) return;

    const productId = button.dataset.productId!;
    const action = button.dataset.action!;
    const input = document.getElementById(`qty-${productId}`) as HTMLInputElement;

    let currentQty = state.selections.get(productId) || 0;
    const wasEmpty = getTotalItemCount() === 0; // Check if cart was empty before

    if (action === 'increase' && currentQty < 10) {
        currentQty++;
    } else if (action === 'decrease' && currentQty > 0) {
        currentQty--;
    }

    state.selections.set(productId, currentQty);
    input.value = currentQty.toString();

    updateOrderSummary();
    updateCheckoutButton();
    updateCartBadge();

    // Show "View Cart" prompt if this is the first item added
    if (wasEmpty && getTotalItemCount() > 0) {
        showViewCartPrompt();
    }
}

function handleImagePreview(e: Event) {
    const img = e.target as HTMLImageElement;
    const productId = img.dataset.productId;

    if (!productId) return;

    const product = state.products.find(p => p.id === productId);

    if (!product) return;

    openImagePreview(product);
}

function openImagePreview(product: Product) {
    const modal = document.getElementById('image-preview-modal') as HTMLDialogElement;
    const previewImage = document.getElementById('preview-image') as HTMLImageElement;
    const previewName = document.getElementById('preview-product-name');
    const previewPrice = document.getElementById('preview-product-price');
    const previewDescription = document.getElementById('preview-product-description');

    if (!modal || !previewImage || !previewName || !previewPrice || !previewDescription) return;

    // Populate modal with product data
    previewImage.src = product.image_url;
    previewImage.alt = product.name;
    previewName.textContent = product.name;
    previewPrice.textContent = product.formatted_price;
    previewDescription.textContent = product.description;

    // Show modal
    modal.showModal();
}
